import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/** What the Evidence card reads off the submission. */
export interface EvidenceFields {
  linkedInUrl: string | null
  linkedInOptedOut: boolean
  leetcodeUrl: string | null
  leetcodeNotes: string | null
  cvFileName: string | null
}

const ABSENT = '—'

/**
 * A profile link, shown the way a reviewer scans it: no scheme, no `www.`,
 * no trailing slash. Only an http(s) value becomes a link. Anything else in
 * the column is shown as text and never handed to `href`, because a column
 * is a claim nothing re-checks on the way out.
 */
function ProfileLink({ url }: { url: string }) {
  if (!/^https?:\/\//i.test(url))
    return <span className="break-all">{url}</span>
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="break-all underline underline-offset-4"
    >
      {url.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '')}
      <span className="sr-only"> (opens in a new tab)</span>
    </a>
  )
}

function EvidenceRow({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="grid grid-cols-[90px_1fr] gap-2 py-1.5">
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  )
}

/**
 * The evidence a reviewer opens rather than reads (LG-1030).
 *
 * Always rendered, with a dash for anything absent, so a submission written
 * before any of these existed reads as a row of dashes, never as an error.
 * The LeetCode link used to appear only when set, and then "they left it
 * blank" looked exactly like "I have not scrolled far enough".
 */
export function EvidenceCard({ evidence }: { evidence: EvidenceFields }) {
  return (
    <section
      aria-labelledby="audition-evidence-heading"
      className="bg-card rounded-md border p-4"
    >
      <h2
        id="audition-evidence-heading"
        className="text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wide"
      >
        Evidence
      </h2>
      <dl className="divide-border divide-y text-sm">
        <EvidenceRow label="LinkedIn">
          {evidence.linkedInUrl ? (
            <ProfileLink url={evidence.linkedInUrl} />
          ) : evidence.linkedInOptedOut ? (
            // Said outright, in red. The applicant ticked "I don't have a
            // LinkedIn profile", which counts against them. It is not a blank.
            <span className="text-destructive">
              No LinkedIn profile (opted out)
            </span>
          ) : (
            ABSENT
          )}
        </EvidenceRow>
        <EvidenceRow label="LeetCode">
          {/* Notes save without a link, so either one is enough to show. */}
          {evidence.leetcodeUrl || evidence.leetcodeNotes ? (
            <>
              {evidence.leetcodeUrl && (
                <ProfileLink url={evidence.leetcodeUrl} />
              )}
              {evidence.leetcodeNotes && (
                // Typed in a textarea, and shown in a column that scrolls: keep
                // the line breaks, and break a pasted handle rather than let it
                // scroll the whole column sideways.
                <span
                  className={cn(
                    'text-muted-foreground block whitespace-pre-wrap break-words text-xs',
                    evidence.leetcodeUrl && 'mt-0.5',
                  )}
                >
                  {evidence.leetcodeNotes}
                </span>
              )}
            </>
          ) : (
            ABSENT
          )}
        </EvidenceRow>
        <EvidenceRow label="CV">
          <div className="flex items-center justify-between gap-2">
            <span className="min-w-0 truncate">
              {evidence.cvFileName ?? ABSENT}
            </span>
            {/* Inert until file storage exists (LG-918). Disabled rather
                than wired to nothing, so it can never look like a download
                that silently failed. */}
            <Button type="button" variant="outline" size="sm" disabled>
              Download
            </Button>
          </div>
          <p className="text-muted-foreground mt-1 text-xs">
            Downloads arrive with file storage.
          </p>
        </EvidenceRow>
      </dl>
    </section>
  )
}
